import React, {useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {MemoryBitacoraView} from '../../memory/MemoryBitacoraView';
import {HistorySeismograph, shortDayLabel} from './HistorySeismograph';

/**
 * La bandeja "Historial de plataformas": el pulso de los últimos 14 días arriba y la Bitácora
 * de siempre debajo.
 *
 * La Bitácora NO se reescribe: se RE-ALOJA. Ya tiene capas, filtros por línea, búsqueda, "Ver
 * detalle técnico", cargar-más y el deep-link con el que el Panel, el Listado y el Command Center
 * la abren filtrada. Reimplementarla acá sería tener dos historiales que contestan distinto sobre
 * los mismos eventos.
 *
 * UN SOLO filtro de fecha. En el mockup el día del sismógrafo y el rango de fechas eran dos
 * controles que no se hablaban: se podía elegir el día 8 con un rango que lo excluía y quedarse
 * mirando un vacío inexplicable. Acá el click en una barra SETEA el rango de la Bitácora
 * (`appliedRange`), no convive con otro; y mientras hay un día fijado se dice cuál es y cómo
 * sacarlo, porque el estado de un filtro no puede vivir sólo en el color de una barra.
 *
 * La Bitácora va envuelta en `iabm-history__list`, que la re-tematiza al lienzo con overrides
 * scopeados a ese contenedor (no la reescribe: `ActivityLogEntry` se usa en otras cuatro pantallas
 * y ahí sigue clara). Dejarla clara acá decía que el Historial es de otra naturaleza que los
 * Hechos, contradiciendo el copy del duo — "Tu IA lee las dos cosas juntas".
 *
 * UN SOLO universo de eventos. El pulso lo pide el duo (el número tiene que estar en la tarjeta
 * antes de que el usuario abra esta bandeja), pero quién decide QUÉ se cuenta son los chips de
 * capas de la Bitácora — que están acá abajo. Por eso la bandeja hace de cable: le pasa a la
 * Bitácora el `onActiveCategoriesChange` del duo, así prender o apagar un chip vuelve a pedir el
 * pulso con las capas nuevas y la barra de un día sigue valiendo lo que ese día lista.
 *
 * Los filtros de quién / cuenta / tipo de evento viajan por el MISMO cable
 * (`onServerFiltersChange`) y por la misma razón: con «Quién = API de Meta» puesto, un pulso que
 * lo ignorara seguiría contando además todo lo que hicieron las personas, y cada barra abriría
 * un día con menos filas de las que promete. Es el tercer intento de la misma igualdad.
 */
export function MemoryHistoryTray({clientId, days, onCategoriesChange, onServerFiltersChange}) {
    const {t} = useTranslation();
    const [selectedDate, setSelectedDate] = useState(null);

    // Referencia estable: la Bitácora compara la prop para decidir si re-consulta. Un objeto nuevo
    // en cada render la haría refetchear con los mismos valores.
    const appliedRange = useMemo(
        () => (selectedDate ? {from: selectedDate, to: selectedDate} : null),
        [selectedDate],
    );

    return (
        <div className="iabm-history">
            {/* La cabecera de la pestaña, gemela de la del archivo (mockup: `memtab-head`): el
                punto celeste y la bajada son lo que hace evidente, sin leer, que la bandeja cambió
                de cara. El copy dice lo que esta bandeja MUESTRA — la Bitácora abre con seis de
                sus siete capas, no sólo cambios de plataforma —, así que no repite el "viene por
                API" del mockup, que describiría una sola de ellas. */}
            <div className="iabm-tabhead">
                <span className="iabm-tabhead__flag">
                    <span className="iabm-tabhead__dot" aria-hidden="true"/>
                    {t('ia_booster.history.head_flag')}
                </span>
                <span className="iabm-tabhead__src">
                    {t('ia_booster.history.head_src')} <b>{t('ia_booster.history.head_src_em')}</b>
                </span>
            </div>
            <HistorySeismograph
                days={days}
                selectedDate={selectedDate}
                onSelectDate={setSelectedDate}
            />
            {selectedDate && (
                <div className="iabm-history__pin">
                    <span className="iabm-history__pin-text">
                        {t('ia_booster.seismo.pinned', {
                            date: shortDayLabel(selectedDate),
                            interpolation: {escapeValue: false},
                        })}
                    </span>
                    <button type="button" className="iabm-history__pin-clear"
                            onClick={() => setSelectedDate(null)}>
                        {t('ia_booster.seismo.pinned_clear')}
                    </button>
                </div>
            )}
            <div className="iabm-history__list">
                {/* `onClearAppliedRange`: "Limpiar filtros" vive adentro de la Bitácora, pero el
                    día elegido lo fija el sismógrafo y se muestra ACÁ. Sin este aviso, limpiar
                    dejaría la barra marcada y el cartel diciendo "estás viendo el 12 de agosto"
                    sobre una lista que ya muestra todo. */}
                <MemoryBitacoraView clientId={clientId} appliedRange={appliedRange}
                                    onActiveCategoriesChange={onCategoriesChange}
                                    onServerFiltersChange={onServerFiltersChange}
                                    onClearAppliedRange={() => setSelectedDate(null)}/>
            </div>
        </div>
    );
}

export default MemoryHistoryTray;
